import './MyPlays.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IntegrationBadges from '../components/IntegrationBadges';
import { fetchPlays, fetchEntitlements, pausePlay, archivePlay } from '../components/Plays/playsApi';
import LaunchPlayButton from '../components/Plays/LaunchPlayButton';
import PlaysMeter, { PlayUsagePolicy } from '../components/Plays/PlaysMeter';
import { dedupFetch } from '../utils/dedupFetch';

// Same dashboard API host used by Impact.jsx / RevenueFinder.
const IMPACT_API_BASE = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';
const getCompanyId = (c) => c?.CompanyId || c?.companyId || c?.company_id || c?.id || '';

// Same Klaviyo status host used by IntegrationBadges.jsx. The backend that
// feeds /impact/summary and /impact/plays does NOT know about email
// platforms yet (klaviyo_confirmed is the only number it computes today,
// Klaviyo or not) — that's a backend gap, not something to patch here. What
// we CAN do honestly from the frontend is reuse the same /klaviyo/status
// signal IntegrationBadges already fetches (dedupFetch collapses the
// duplicate call) to know whether THIS company is actually on Klaviyo. If it
// is, keep the "Confirmed by Klaviyo" label exactly as before. If it is not
// (Omnisend or anything else, or unknown), stop claiming Klaviyo — there is
// no has_omnisend signal anywhere yet, so we cannot show an
// Omnisend-specific message without inventing data. We generalize the label
// instead of guessing.
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const KLAVIYO_API_BASE = isLocalhost
  ? 'http://localhost:8000'
  : 'https://mi-chat-892931479853.us-central1.run.app';

// Billing controls (plays list, entitlements, pause/archive/launch) are only
// meaningful when the billing UI is on. With the flag off this page degrades
// gracefully to impact-only metrics, matching SegmentCounts.jsx's pattern.
const BILLING_UI_ENABLED = process.env.REACT_APP_ENABLE_BILLING === 'true';

function fmtMoney(v) {
  if (v == null || isNaN(Number(v))) return '--';
  return '$' + Math.round(Number(v)).toLocaleString();
}

// ---------------------------------------------------------------------------
// Impact mocks (ported from Impact.jsx): the fetch calls are wired to the
// real routes; when they fail or 404 we fall back to these so the page still
// renders end to end. No unlabeled mocks — usingMock flags the banner.
// ---------------------------------------------------------------------------
const MOCK_SUMMARY = {
  window_start: '2026-06-22',
  window_end: '2026-07-19',
  total_correlated_revenue: 18450,
  total_correlated_orders: 96,
  klaviyo_confirmed: 6120,
  plays_run: 5,
  campaigns_sent: 9,
  plan_cost: null,
};

const MOCK_IMPACT_PLAYS = [
  {
    play_id: 'winback-core',
    play_name: 'Win-back Lapsed High Spenders',
    play_type: 'always_on',
    status: 'performing',
    day: 34,
    revenue_7d: 2140,
    revenue_14d: 4310,
    orders_7d: 18,
    orders_14d: 39,
    klaviyo_attributed: 1650,
    weekly: [
      { week: '2026-06-22', revenue: 1480 },
      { week: '2026-06-29', revenue: 1720 },
      { week: '2026-07-06', revenue: 1890 },
      { week: '2026-07-13', revenue: 2140 },
    ],
    campaigns: [{ campaign_id: 'c1', campaign_name: 'Winback wave 3' }],
  },
  {
    play_id: 'second-purchase',
    play_name: 'Second Purchase Nudge',
    play_type: 'always_on',
    status: 'stalled',
    day: 51,
    revenue_7d: 120,
    revenue_14d: 480,
    orders_7d: 2,
    orders_14d: 6,
    klaviyo_attributed: 90,
    weekly: [
      { week: '2026-06-22', revenue: 610 },
      { week: '2026-06-29', revenue: 520 },
      { week: '2026-07-06', revenue: 360 },
      { week: '2026-07-13', revenue: 120 },
    ],
    campaigns: [],
  },
];

const MOCK_METHODOLOGY = {
  title: 'How we measure this',
  sections: [
    {
      heading: 'Correlated revenue',
      body: 'We compare purchases from the customers in each Play audience during the measurement window against their baseline behavior. Revenue shown is the total purchased by those customers after the Play launched. It is a correlation, not a controlled experiment.',
    },
    {
      heading: 'Klaviyo confirmed revenue',
      body: 'Revenue that Klaviyo attributes to direct clicks and opens on the campaigns connected to each Play. It is a subset of correlated revenue and the most conservative number on this page.',
    },
    {
      heading: 'Measurement windows',
      body: 'One-time Plays are measured for 14 days after launch, then marked final. Always-on Plays are tracked week over week; a Play is flagged as declining or stalled when its weekly revenue trend drops.',
    },
  ],
};

const CONTROL_STATUS_LABELS = { draft: 'Draft', active: 'Active', paused: 'Paused', archived: 'Archived' };
const IMPACT_STATUS_LABELS = { measuring: 'Measuring', final: 'Final', performing: 'Performing', declining: 'Declining', stalled: 'Stalled' };

function StatusPill({ status, source }) {
  const label = source === 'control' ? (CONTROL_STATUS_LABELS[status] || status) : (IMPACT_STATUS_LABELS[status] || status);
  return <span className={`mp-pill mp-pill--${source}-${status}`}>{label}</span>;
}

function TrendMini({ weekly }) {
  if (!weekly || weekly.length < 2) return null;
  const values = weekly.map((w) => Number(w.revenue) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const W = 90;
  const H = 26;
  const step = W / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(H - 3 - ((v - min) / range) * (H - 6)).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="mp-sparkline" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={points} fill="none" stroke="#8200FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MethodologyModal({ methodology, isMock, onClose }) {
  const data = methodology || MOCK_METHODOLOGY;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={data.title}>
        <div className="mp-modal-head">
          <h3>{data.title}</h3>
          <button type="button" className="mp-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {isMock && <div className="mp-mock-note">Sample methodology text while measurement is being connected</div>}
        {(data.sections || []).map((s, i) => (
          <div className="mp-modal-section" key={i}>
            <h4>{s.heading}</h4>
            <p>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// A single row in the list. `play` is the merged record: billing fields
// (id, name, objective, type, status, campaigns_run, correlated_revenue_30d)
// plus impact fields (play_id, play_name, play_type, status, day, revenue_7d,
// revenue_14d, klaviyo_attributed, weekly, campaigns), each optional.
function PlayRow({
  play, token, entitlements, busyPlayId, onPause, onArchive, onActivated, onNavigateToPricing,
}) {
  const [open, setOpen] = useState(false);
  const hasControl = Boolean(play.controlId);
  const hasImpact = Boolean(play.impact);
  const impact = play.impact || {};
  const name = play.name || impact.play_name || 'Untitled Play';
  const type = play.type || impact.play_type || null;
  const typeLabel = type === 'always_on' ? 'Always-on' : type === 'one_time' ? 'One-time' : null;
  const controlStatus = play.status; // draft/active/paused/archived
  const impactStatus = impact.status; // measuring/final/performing/declining/stalled
  const isBusy = busyPlayId === play.controlId;

  const isFinal = impactStatus === 'final';
  const klaviyoRevenue = hasImpact ? Number(impact.klaviyo_attributed) || 0 : null;
  const ourRevenue = hasImpact
    ? (isFinal
        ? Number(impact.revenue_14d) || 0
        : (type === 'always_on' && impact.weekly && impact.weekly.length
            ? Number(impact.weekly[impact.weekly.length - 1].revenue) || 0
            : Number(impact.revenue_7d) || 0))
    : null;
  const orders = hasImpact ? (isFinal ? (impact.orders_14d ?? 0) : (impact.orders_7d ?? 0)) : null;

  const detailId = `mp-row-detail-${String(play.key).replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="mp-row-wrap">
      <button
        type="button"
        className={`mp-row${open ? ' mp-row--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
      >
        <span className="mp-row-name-wrap">
          <span className="mp-row-name">{name}</span>
          {typeLabel && <span className="mp-tag">{typeLabel}</span>}
          {hasControl && <StatusPill status={controlStatus} source="control" />}
          {hasImpact && <StatusPill status={impactStatus} source="impact" />}
        </span>

        <span className="mp-row-value">
          {hasImpact ? (
            <>
              <span className="mp-row-value-total">{fmtMoney(ourRevenue)}</span>
              <span className="mp-row-value-per">
                {fmtMoney(klaviyoRevenue)} Klaviyo · {orders} orders
              </span>
            </>
          ) : (
            <span className="mp-row-measuring">Measuring</span>
          )}
        </span>

        <span className="mp-row-control">
          <span className={`mp-row-chevron${open ? ' mp-row-chevron--open' : ''}`} aria-hidden="true">▾</span>
        </span>
      </button>

      {open && (
        <div id={detailId} className="mp-row-detail">
          {(play.objective || impact.description) && (
            <p className="mp-row-detail-desc">{play.objective || impact.description}</p>
          )}
          {impact.activated_at && (
            <p className="mp-row-detail-meta">
              Started {new Date(impact.activated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
          {typeof play.segment === 'string' && play.segment.trim() !== '' && (
            <p className="mp-row-detail-meta">Segment: {play.segment}</p>
          )}
          {typeof play.segment === 'string' && play.segment.trim() !== '' && (play.audience_size ?? null) !== null && (
            <p className="mp-row-detail-meta">Estimated audience: {Number(play.audience_size).toLocaleString()} customers</p>
          )}
          {impact.weekly && impact.weekly.length > 1 && (
            <div className="mp-row-detail-trend">
              <TrendMini weekly={impact.weekly} />
              <span className="mp-row-detail-trend-label">Weekly trend</span>
            </div>
          )}
          {impact.campaigns && impact.campaigns.length > 0 && (
            <div className="mp-row-detail-campaigns">
              <span className="mp-row-detail-campaigns-label">Campaigns:</span>{' '}
              {impact.campaigns.map((c) => c.campaign_name).join(', ')}
            </div>
          )}

          {type && <PlayUsagePolicy type={type} />}

          {hasControl && (
            <div className="mp-row-actions">
              {controlStatus === 'draft' && BILLING_UI_ENABLED && (
                <LaunchPlayButton
                  token={token}
                  playId={play.controlId}
                  entitlements={entitlements}
                  canActivate={entitlements?.can_activate}
                  onActivated={onActivated}
                  onNavigateToPricing={onNavigateToPricing}
                />
              )}
              {controlStatus === 'active' && (
                <button type="button" className="mp-action-btn" disabled={isBusy} onClick={() => onPause(play.controlId)}>
                  {isBusy ? 'Working...' : 'Pause'}
                </button>
              )}
              {(controlStatus === 'active' || controlStatus === 'paused') && (
                <button type="button" className="mp-action-btn mp-action-btn--danger" disabled={isBusy} onClick={() => onArchive(play.controlId)}>
                  {isBusy ? 'Working...' : 'Archive'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyPlays({ companyInfo, token, onNavigate }) {
  const companyId = getCompanyId(companyInfo);

  // Billing (control) state.
  const [billingPlays, setBillingPlays] = useState(BILLING_UI_ENABLED ? null : []); // null = loading
  const [entitlements, setEntitlements] = useState(null);
  const [billingError, setBillingError] = useState('');
  const [busyPlayId, setBusyPlayId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Impact (measurement) state.
  const [summary, setSummary] = useState(null);
  const [impactPlays, setImpactPlays] = useState(null); // null = loading
  const [impactError, setImpactError] = useState('');
  const [usingMockImpact, setUsingMockImpact] = useState(false);
  const [methodology, setMethodology] = useState(null);
  const [methodologyIsMock, setMethodologyIsMock] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  const [filter, setFilter] = useState('all'); // all | active | paused | archived

  // klaviyoConnected: null = unknown/loading, true = confirmed Klaviyo,
  // false = confirmed NOT Klaviyo (could be Omnisend or nothing connected;
  // we don't have a signal to tell those apart yet). Same endpoint
  // IntegrationBadges uses, so the "Confirmed by Klaviyo" claim in the hero
  // is only made when it's actually true for this company.
  //
  // klaviyoStatusError: true only when the /klaviyo/status fetch itself
  // failed or returned a non-ok/empty response (network error, 4xx/5xx,
  // malformed body). This is distinct from a genuinely loading state: while
  // klaviyoConnected is still null AND klaviyoStatusError is false, we treat
  // it as "loading" (transient, brief) and keep today's behavior. Once we
  // know the fetch failed, we must NOT keep treating null as "confirmed" --
  // that would silently re-show klaviyo_confirmed (a Klaviyo-only number,
  // possibly 0 or wrong) under a generic label for a real error case. On a
  // fetch error we fall back to the same conservative branch as
  // klaviyoConnected === false (UPSELLerate measured as the protagonist).
  const [klaviyoConnected, setKlaviyoConnected] = useState(null);
  const [klaviyoStatusError, setKlaviyoStatusError] = useState(false);
  useEffect(() => {
    if (!companyId || !token) return undefined;
    let cancelled = false;
    dedupFetch(`${KLAVIYO_API_BASE}/klaviyo/status?company=${encodeURIComponent(companyId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setKlaviyoConnected(Boolean(data.connected));
        } else {
          setKlaviyoStatusError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setKlaviyoStatusError(true);
      });
    return () => { cancelled = true; };
  }, [companyId, token]);

  // Treat a confirmed fetch error the same as "confirmed not Klaviyo" for
  // hero purposes -- both mean we must not attribute klaviyo_confirmed to a
  // real platform confirmation.
  const klaviyoUnavailable = klaviyoConnected === false || klaviyoStatusError;

  const loadBilling = useCallback(async () => {
    if (!BILLING_UI_ENABLED || !token) {
      setBillingPlays([]);
      return;
    }
    setBillingError('');
    try {
      const [playsData, entitlementsData] = await Promise.all([
        fetchPlays({ token }),
        fetchEntitlements({ token }),
      ]);
      setBillingPlays(playsData?.plays || []);
      setEntitlements(entitlementsData);
    } catch (err) {
      setBillingError(err?.message || 'Could not load your Plays controls.');
      setBillingPlays([]);
    }
  }, [token]);

  const loadImpact = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const q = `?company=${encodeURIComponent(companyId)}`;
    try {
      const [sRes, pRes, mRes] = await Promise.all([
        fetch(`${IMPACT_API_BASE}/impact/summary${q}`, { headers }),
        fetch(`${IMPACT_API_BASE}/impact/plays${q}&status=all`, { headers }),
        fetch(`${IMPACT_API_BASE}/impact/methodology`, { headers }),
      ]);
      if (!sRes.ok || !pRes.ok) throw new Error('impact API not available');
      setSummary(await sRes.json());
      const p = await pRes.json();
      setImpactPlays(Array.isArray(p) ? p : p.plays || []);
      if (mRes.ok) {
        setMethodology(await mRes.json());
        setMethodologyIsMock(false);
      } else {
        setMethodology(MOCK_METHODOLOGY);
        setMethodologyIsMock(true);
      }
      setUsingMockImpact(false);
      setImpactError('');
    } catch (e) {
      setSummary(MOCK_SUMMARY);
      setImpactPlays(MOCK_IMPACT_PLAYS);
      setMethodology(MOCK_METHODOLOGY);
      setUsingMockImpact(true);
      setMethodologyIsMock(true);
      setImpactError('Live results are unavailable right now; showing sample data.');
    }
  }, [companyId, token]);

  useEffect(() => { loadBilling(); }, [loadBilling, refreshKey]);
  useEffect(() => { loadImpact(); }, [loadImpact]);

  const handlePause = async (playId) => {
    setBusyPlayId(playId);
    try {
      await pausePlay({ token, playId });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setBillingError(err?.message || 'Could not pause this Play.');
    } finally {
      setBusyPlayId(null);
    }
  };

  const handleArchive = async (playId) => {
    if (!window.confirm('Archive this play? It will stop counting toward your results.')) return;
    setBusyPlayId(playId);
    try {
      await archivePlay({ token, playId });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setBillingError(err?.message || 'Could not archive this Play.');
    } finally {
      setBusyPlayId(null);
    }
  };

  // Join: billing.id === impact.play_id, fallback by name. A billing play
  // with no impact match still appears (controls only, "Measuring"). An
  // impact play with no billing match still appears (metrics only, no
  // controls). Nothing disappears because of the join.
  const merged = useMemo(() => {
    const bPlays = billingPlays || [];
    const iPlays = impactPlays || [];
    const usedImpactIds = new Set();

    // Pass 1: exact id match, consuming each impact play at most once.
    const byId = new Map();
    bPlays.forEach((bp) => {
      if (byId.has(bp.id)) return;
      const match = iPlays.find((ip) => ip.play_id === bp.id && !usedImpactIds.has(ip.play_id));
      if (match) {
        usedImpactIds.add(match.play_id);
        byId.set(bp.id, match);
      }
    });

    // Pass 2: fallback by name, only over impact plays not already claimed
    // by id. If multiple billing plays share a name, prefer the active one,
    // then the most recently activated, so an impact play attaches to at
    // most one row.
    const byName = new Map();
    const nameGroups = new Map();
    bPlays.forEach((bp) => {
      if (byId.has(bp.id)) return;
      const key = (bp.name || '').trim().toLowerCase();
      if (!key) return;
      if (!nameGroups.has(key)) nameGroups.set(key, []);
      nameGroups.get(key).push(bp);
    });
    nameGroups.forEach((group, key) => {
      const match = iPlays.find((ip) => !usedImpactIds.has(ip.play_id) && (ip.play_name || '').trim().toLowerCase() === key);
      if (!match) return;
      const ranked = [...group].sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (b.status === 'active' && a.status !== 'active') return 1;
        const aDate = a.activated_at ? new Date(a.activated_at).getTime() : 0;
        const bDate = b.activated_at ? new Date(b.activated_at).getTime() : 0;
        return bDate - aDate;
      });
      const winner = ranked[0];
      usedImpactIds.add(match.play_id);
      byName.set(winner.id, match);
    });

    const rows = bPlays.map((bp) => {
      const match = byId.get(bp.id) || byName.get(bp.id) || null;
      return {
        key: `b-${bp.id}`,
        controlId: bp.id,
        name: bp.name,
        objective: bp.objective,
        type: bp.type,
        status: bp.status,
        campaigns_run: bp.campaigns_run,
        correlated_revenue_30d: bp.correlated_revenue_30d,
        impact: match,
      };
    });
    iPlays.forEach((ip) => {
      if (usedImpactIds.has(ip.play_id)) return;
      rows.push({
        key: `i-${ip.play_id}`,
        controlId: null,
        name: ip.play_name,
        objective: null,
        type: ip.play_type,
        status: null,
        impact: ip,
      });
    });
    return rows;
  }, [billingPlays, impactPlays]);

  const filtered = useMemo(() => {
    if (filter === 'all') return merged.filter((p) => p.status !== 'archived');
    if (filter === 'archived') return merged.filter((p) => p.status === 'archived');
    return merged.filter((p) => p.status === filter);
  }, [merged, filter]);

  const isLoading = billingPlays === null || impactPlays === null;
  const hasAnyPlays = merged.length > 0;

  return (
    <div className="mp-page">
      <div className="mp-header">
        <IntegrationBadges companyInfo={companyInfo} token={token} />
        <h2 className="mp-title">My Plays</h2>

        {summary && (
          <>
            {klaviyoUnavailable ? (
              // Either confirmed NOT on Klaviyo, or the /klaviyo/status fetch
              // itself failed (network error, non-ok response). Either way,
              // summary.klaviyo_confirmed still comes from the backend's
              // Klaviyo-only calculation, so we stop showing it as a number
              // (that would be a $0 or stale figure wrongly attributed to
              // "Klaviyo") and lead with our own measurement instead. No
              // claim about Omnisend specifically -- we don't have that
              // signal, only "not Klaviyo" or "unknown due to error".
              <div className="mp-hero">
                <div className="mp-hero-metric mp-hero-metric--primary">
                  <div className="mp-hero-amount">{fmtMoney(summary.total_correlated_revenue)}</div>
                  <div className="mp-hero-label">Measured by UPSELLerate</div>
                </div>
                <div className="mp-hero-metric mp-hero-metric--secondary">
                  <div className="mp-hero-label mp-hero-label--pending">Third-party confirmation coming soon</div>
                </div>
              </div>
            ) : (
              <div className="mp-hero">
                <div className="mp-hero-metric mp-hero-metric--primary">
                  <div className="mp-hero-amount">{fmtMoney(summary.klaviyo_confirmed)}</div>
                  <div className="mp-hero-label">
                    {klaviyoConnected === true ? 'Confirmed by Klaviyo' : 'Confirmed by your email platform'}
                  </div>
                </div>
                <div className="mp-hero-metric mp-hero-metric--secondary">
                  <div className="mp-hero-amount">{fmtMoney(summary.total_correlated_revenue)}</div>
                  <div className="mp-hero-label">Measured by UPSELLerate</div>
                </div>
              </div>
            )}
            {summary.plan_cost != null && Number(summary.plan_cost) > 0 && !klaviyoUnavailable && (
              <p className="mp-hero-roi">
                That's {fmtMoney(summary.klaviyo_confirmed)} generated vs your {fmtMoney(summary.plan_cost)} plan this month
              </p>
            )}
            {summary.plan_cost != null && Number(summary.plan_cost) > 0 && klaviyoUnavailable && (
              <p className="mp-hero-roi">
                That's {fmtMoney(summary.total_correlated_revenue)} generated vs your {fmtMoney(summary.plan_cost)} plan this month
              </p>
            )}
            {summary.plays_run != null && (
              <p className="mp-hero-sub">{summary.plays_run} plays run · {summary.campaigns_sent ?? 0} campaigns sent</p>
            )}
            {usingMockImpact && <div className="mp-mock-note">Sample data shown while measurement is being connected</div>}
          </>
        )}

        {BILLING_UI_ENABLED && (
          <div className="mp-meter-wrap">
            <PlaysMeter token={token} entitlements={entitlements} refreshKey={refreshKey} onNavigateToPricing={() => onNavigate?.('pricing')} />
          </div>
        )}

        {impactError && !usingMockImpact && (
          <div className="mp-source-warning">{impactError}</div>
        )}
        {billingError && (
          <div className="mp-source-warning">{billingError}{!BILLING_UI_ENABLED ? '' : ' Controls may be unavailable.'}</div>
        )}
      </div>

      {isLoading ? (
        <div className="mp-skeleton-list">
          {[0, 1, 2].map((i) => <div key={i} className="mp-row-skeleton" />)}
        </div>
      ) : !hasAnyPlays ? (
        <div className="mp-empty">
          <div className="mp-empty-title">No Plays yet</div>
          <p>When you launch your first Play, this is where you will track it and see the revenue it brings back.</p>
          <button type="button" className="mp-btn mp-btn--primary" onClick={() => onNavigate?.('revenue')}>
            Go to Revenue Plays
          </button>
        </div>
      ) : (
        <>
          <div className="mp-filter-bar">
            {['all', 'active', 'paused', 'archived'].map((f) => (
              <button
                key={f}
                type="button"
                className={`mp-filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : f === 'paused' ? 'Paused' : 'Archived'}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="mp-section-empty">No Plays match this filter.</p>
          ) : (
            <div className="mp-list">
              {filtered.map((play) => (
                <PlayRow
                  key={play.key}
                  play={play}
                  token={token}
                  entitlements={entitlements}
                  busyPlayId={busyPlayId}
                  onPause={handlePause}
                  onArchive={handleArchive}
                  onActivated={() => setRefreshKey((k) => k + 1)}
                  onNavigateToPricing={() => onNavigate?.('pricing')}
                />
              ))}
            </div>
          )}
        </>
      )}

      <div className="mp-footer">
        <button type="button" className="mp-link" onClick={() => setShowMethodology(true)}>
          How we measure this
        </button>
      </div>

      {showMethodology && (
        <MethodologyModal methodology={methodology} isMock={methodologyIsMock} onClose={() => setShowMethodology(false)} />
      )}
    </div>
  );
}
